// Chat.js
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../api/axios';
import EventTypeMessage from './EventTypeMessage';
import VenueList from './venue/VenueList';

function getSessionCookie(name) {
    const nameEQ = name + "=";
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let c = cookies[i].trim();
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    return null;
  }

const generateUrlSafeId = (length = 21) => {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);

  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
    .slice(0, length);
};

function Chat() {
  const [messages, setMessages] = useState([
    { 
      sender: 'AI Assistant', 
      type: 'event-select', 
      text: 'Please select an event type to begin'
    }
  ]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [eventType, setEventType] = useState('');
  const messagesEndRef = useRef(null);
  const eventTypes = ['birthday', 'wedding', 'meeting', 'party', 'conference'];

  //const sessionId = getSessionCookie('sessionId');
  //const sessionId = generateUrlSafeId();
	//

  // Function to update messages
  const updateMessages = (newMessage) => {
    setMessages([...messages, { sender: 'AI Assistant', text: newMessage }]);
  };

  const handleEventSelect = async (eventType) => {
    setMessages([
      ...messages,
      { 
        sender: 'user', 
        text: `Selected event: ${eventType}` 
      }
    ]);
    
    try {
      // Replace with your Dialogflow API endpoint
      const response = await axiosInstance.post('/chats', {
        message: 'I am planning a ' + eventType + ' event',
        session_id: sessionId
      });

      // Get the response text from Dialogflow
      const botMessage = response.data.response;
      const results = response.data.results;
      if (results) {
        setMessages([...messages, { sender: 'AI Assistant', text: botMessage, results: results }]);
      }
      else {
        setMessages([...messages, { sender: 'AI Assistant', text: botMessage }]);
      }
      setInput(''); // Clear input field
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // useEffect(() => {
  //   const sessionId = localStorage.getItem('sessionId');
  //   if (sessionId) {
  //     setSessionId(sessionId);
  //   } else {
  //     const newId = generateUrlSafeId();
  //     localStorage.setItem('sessionId', newId);
  //     setSessionId(newId);
  //   }
  // }, [sessionId]);

  useEffect(() => {
    const isPageRefresh = performance.getEntriesByType('navigation')[0].type === 'reload';
    const existingId = localStorage.getItem('sessionId');
    
    if (isPageRefresh || !existingId) {
      const newId = generateUrlSafeId();
      localStorage.setItem('sessionId', newId);
      setSessionId(newId);
    } else {
      setSessionId(existingId);
    }
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (input.trim() === '') return;

    // Add the user message to the chat
    setMessages([...messages, { sender: 'user', text: input }]);

    try {
      // Replace with your Dialogflow API endpoint
      const response = await axiosInstance.post('/chats', {
        message: input,
        session_id: sessionId
      });

      // Get the response text from Dialogflow
      const botMessage = response.data.response;
      const results = response.data.results;
      if (results) {
        setMessages([...messages, { sender: 'user', text: input }, { sender: 'AI Assistant', text: botMessage, results: results }]);
      }
      else {
        setMessages([...messages, { sender: 'user', text: input }, { sender: 'AI Assistant', text: botMessage }]);
      }
      setInput(''); // Clear input field
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-white rounded-lg shadow-lg border border-gray-200">
      <div className="h-[500px] overflow-y-auto p-4">
        <div className="space-y-4">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex items-start gap-3 ${
                msg.sender === 'user' ? 'flex-row-reverse' : ''
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white
                ${msg.sender === 'user' ? 'bg-blue-500' : 'bg-green-500'}`}>
                {msg.sender === 'user' ? 'Y' : 'AI'}
              </div>
              
              <div className={`flex-1 max-w-[80%] px-4 py-2 rounded-lg 
                ${msg.sender === 'user' 
                  ? 'bg-blue-100 rounded-tr-none ml-auto' 
                  : 'bg-gray-100 rounded-tl-none'
                }`}>
                {msg.type === 'event-select' ? (
                  <EventTypeMessage onSelect={handleEventSelect} />
                ) : (
                  <p className="text-sm text-gray-900">{msg.text}</p>
                )}
                <VenueList venues={msg.results} updateMessages={updateMessages} />
              </div>
            </div>
          ))}
	  <div ref={messagesEndRef} />
        </div>
      </div>
      
      <div className="p-4 border-t border-gray-200">
        <div className="flex gap-2">
        <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask AI Assistant..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          <button 
            onClick={sendMessage}
            className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
          >
            Ask
          </button>
        </div>
      </div>
    </div>
  );
}

export default Chat;
